import React, { useMemo, useState } from 'react';
import {
  Paper, Typography, Chip, IconButton, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Edit, Save, Close, DeleteOutline } from '@mui/icons-material';
import type { ExpenseCategory, ExpenseSubCategory } from '../../types';
import {
  type ParsedBulkExpenseRow,
  type BulkExpenseEditableFields,
  type BulkExpenseLookups,
  validateBulkExpenseFields,
} from '../../utils/bulkExpensesXlsx';

interface Props {
  rows: ParsedBulkExpenseRow[];
  categories: ExpenseCategory[];
  subCategories: ExpenseSubCategory[];
  lookups: BulkExpenseLookups;
  filter: 'all' | 'ok' | 'error';
  onChange: (next: ParsedBulkExpenseRow[]) => void;
}

const BulkUploadReviewTable: React.FC<Props> = ({
  rows, categories, subCategories, lookups, filter, onChange,
}) => {
  const theme = useTheme();
  const [editingRowNumber, setEditingRowNumber] = useState<number | null>(null);
  const [draft, setDraft] = useState<BulkExpenseEditableFields | null>(null);

  const subCategoriesByCategoryId = useMemo(() => {
    const map = new Map<string, ExpenseSubCategory[]>();
    for (const sc of subCategories) {
      const parentId = typeof sc.categoryId === 'string' ? sc.categoryId : (sc.categoryId as any)._id;
      const bucket = map.get(parentId);
      if (bucket) bucket.push(sc);
      else map.set(parentId, [sc]);
    }
    return map;
  }, [subCategories]);

  const visibleRows = useMemo(() => {
    if (filter === 'ok') return rows.filter((r) => r.clientStatus === 'ok');
    if (filter === 'error') return rows.filter((r) => r.clientStatus === 'error');
    return rows;
  }, [rows, filter]);

  const startEdit = (row: ParsedBulkExpenseRow) => {
    setEditingRowNumber(row.rowNumber);
    setDraft({ ...row.fields });
  };

  const cancelEdit = () => {
    setEditingRowNumber(null);
    setDraft(null);
  };

  const saveEdit = (rowNumber: number) => {
    if (!draft) return;
    const validated = validateBulkExpenseFields(draft, lookups);
    const next = rows.map((r) =>
      r.rowNumber === rowNumber
        ? { ...r, fields: draft, ...validated }
        : r,
    );
    onChange(next);
    setEditingRowNumber(null);
    setDraft(null);
  };

  const deleteRow = (rowNumber: number) => {
    onChange(rows.filter((r) => r.rowNumber !== rowNumber));
  };

  // While editing, if the user changes the category, clear the subcategory
  // because the previous selection may not belong to the new category.
  const onDraftCategoryChange = (categoryName: string) => {
    if (!draft) return;
    setDraft({ ...draft, categoryName, subCategoryName: '' });
  };

  const draftCategoryId = useMemo(() => {
    const name = draft?.categoryName?.trim().toLowerCase();
    if (!name) return undefined;
    return lookups.categoriesByName.get(name)?._id;
  }, [draft?.categoryName, lookups]);

  const draftSubCategoryOptions = useMemo(() => {
    if (!draftCategoryId) return [];
    return subCategoriesByCategoryId.get(draftCategoryId) ?? [];
  }, [draftCategoryId, subCategoriesByCategoryId]);

  if (rows.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4, textAlign: 'center',
          borderRadius: 2, border: `1px dashed ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No rows to review.
        </Typography>
      </Paper>
    );
  }

  if (visibleRows.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4, textAlign: 'center',
          borderRadius: 2, border: `1px dashed ${theme.palette.divider}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {filter === 'ok'
            ? 'No ready-to-upload rows match this filter.'
            : 'No rows with errors — everything looks good.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Sub-category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const isEditing = editingRowNumber === row.rowNumber;
              const isError = row.clientStatus === 'error';
              const tintColor = isError
                ? alpha(theme.palette.error.main, 0.05)
                : 'transparent';
              return (
                <TableRow
                  key={row.rowNumber}
                  hover
                  sx={{
                    bgcolor: tintColor,
                    verticalAlign: 'top',
                  }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {row.rowNumber}
                  </TableCell>
                  <TableCell>
                    {isError ? (
                      <Tooltip title={row.clientError || 'Error'}>
                        <Chip
                          size="small"
                          icon={<ErrorOutline sx={{ fontSize: 16 }} />}
                          label="Error"
                          sx={{
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            fontWeight: 600,
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                        label="Ready"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </TableCell>
                  {isEditing && draft ? (
                    <>
                      <TableCell>
                        <TextField
                          size="small" fullWidth autoFocus
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" type="number" inputProps={{ min: 0, step: '0.01' }}
                          value={draft.amount}
                          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" type="date"
                          value={draft.date ? draft.date.slice(0, 10) : ''}
                          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" select fullWidth
                          value={draft.categoryName ?? ''}
                          onChange={(e) => onDraftCategoryChange(e.target.value)}
                        >
                          <MenuItem value="">—</MenuItem>
                          {categories.map((c) => (
                            <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" select fullWidth
                          value={draft.subCategoryName ?? ''}
                          onChange={(e) => setDraft({ ...draft, subCategoryName: e.target.value })}
                          disabled={!draftCategoryId || draftSubCategoryOptions.length === 0}
                        >
                          <MenuItem value="">—</MenuItem>
                          {draftSubCategoryOptions.map((s) => (
                            <MenuItem key={s._id} value={s.name}>{s.name}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth multiline maxRows={3}
                          value={draft.notes ?? ''}
                          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={() => saveEdit(row.rowNumber)}>
                            <Save fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.fields.title || <em style={{ color: theme.palette.text.disabled }}>missing</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.fields.amount || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.parsedDateIso
                            ? row.parsedDateIso.slice(0, 10)
                            : row.fields.date || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={row.errorField === 'category' ? 'error.main' : 'text.primary'}
                        >
                          {row.fields.categoryName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={row.errorField === 'subCategory' ? 'error.main' : 'text.primary'}
                        >
                          {row.fields.subCategoryName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {row.fields.notes || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit row">
                          <IconButton size="small" onClick={() => startEdit(row)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove row">
                          <IconButton size="small" color="error" onClick={() => deleteRow(row.rowNumber)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BulkUploadReviewTable;
